import { useCallback, useEffect, useRef } from 'react';
import i18n from 'i18next';
import {
    BlockableHistory,
    confirmLeaveWithUnsavedChanges,
    createNavigationBlocker,
    NavigationBlocker,
    NO_NAVIGATION_BLOCKER
} from './unsavedChangesPrompt';

const UNSAVED_CHANGES_MESSAGE_KEY = 'commons.unsavedChangesPrompt';

export interface UnsavedChangesPrompt {
    confirmLeave: () => boolean;
    allowNavigation: () => void;
}

export function useUnsavedChangesPrompt(
    hasUnsavedChanges: boolean,
    history: BlockableHistory,
    onNavigationAllowed?: () => void
): UnsavedChangesPrompt {
    const blocker = useRef(NO_NAVIGATION_BLOCKER);
    const navigationAllowedCallback = useRef(onNavigationAllowed);

    useEffect(() => {
        navigationAllowedCallback.current = onNavigationAllowed;
    });

    useEffect(() => {
        if (!hasUnsavedChanges) return;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };

        const activeBlocker: NavigationBlocker = createNavigationBlocker(history, i18n.t(UNSAVED_CHANGES_MESSAGE_KEY));
        blocker.current = activeBlocker;
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            activeBlocker.release();
            blocker.current = NO_NAVIGATION_BLOCKER;
        };
    }, [hasUnsavedChanges, history]);

    const allowNavigation = useCallback(() => {
        blocker.current.release();
        blocker.current = NO_NAVIGATION_BLOCKER;

        const notifyNavigationAllowed = navigationAllowedCallback.current;
        if (notifyNavigationAllowed) notifyNavigationAllowed();
    }, []);

    const confirmLeave = useCallback(() => {
        const canLeave = confirmLeaveWithUnsavedChanges({
            hasUnsavedChanges,
            message: i18n.t(UNSAVED_CHANGES_MESSAGE_KEY),
            confirm: (message) => window.confirm(message)
        });

        if (canLeave) allowNavigation();
        return canLeave;
    }, [hasUnsavedChanges, allowNavigation]);

    return { confirmLeave, allowNavigation };
}
